import React, {useLayoutEffect, useRef, useState} from 'react';
import {Player} from '../../types';
import SectionHeader from '../SectionHeader';
import PlayerCard from './PlayerCard';
import {prefersReducedMotion, useCardTransitions} from './useCardTransitions';
import './player.css';

const playerKey = (player: Player) => `${player.firstname}-${player.lastname}-${player.number ?? ''}`;

/** Scroll position that puts `element` right below the fixed header (its scroll-margin-top). */
const scrollTopFor = (element: HTMLElement) =>
    element.getBoundingClientRect().top + window.scrollY - (parseFloat(getComputedStyle(element).scrollMarginTop) || 0);

/** The squad, followed by the coaching staff (players marked as trainer). */
const Team = ({players}: { players: Player[] }) => {
    const [openPlayer, setOpenPlayer] = useState<string>();
    const previousPlayer = useRef<string>();
    const transitions = useCardTransitions();

    const toggle = (key: string) => {
        transitions.capture();
        setOpenPlayer(current => (current === key ? undefined : key));
    };

    useLayoutEffect(() => {
        const closedPlayer = previousPlayer.current;
        previousPlayer.current = openPlayer;
        if (closedPlayer === openPlayer) return;

        transitions.stop();

        // an opened card gets focused; after closing, only scroll back if the card is above the viewport
        const focused = openPlayer ?? closedPlayer;
        const element = focused ? transitions.element(focused) : undefined;
        let scrollTop: number | undefined;
        if (element) {
            const target = scrollTopFor(element);
            if (openPlayer || target < window.scrollY) scrollTop = target;
        }

        transitions.play(new Set([closedPlayer, openPlayer].filter((key): key is string => !!key)));
        if (scrollTop !== undefined) {
            window.scrollTo({top: scrollTop, behavior: prefersReducedMotion() ? 'auto' : 'smooth'});
        }
    }, [openPlayer, transitions]);

    const cards = (list: Player[]) => (
        <div className="player-grid">
            {list.map(player => {
                const key = playerKey(player);
                return (
                    <PlayerCard key={key} player={player} isOpen={openPlayer === key}
                                cardRef={transitions.register(key)} onToggle={() => toggle(key)}/>
                );
            })}
        </div>
    );

    const squad = players.filter(player => !player.trainer);
    const staff = players.filter(player => player.trainer);

    return (
        <>
            {squad.length > 0 && cards(squad)}
            {staff.length > 0 && (
                <>
                    {/* registered like a card, so it glides along instead of jumping while the cards above move */}
                    <div ref={transitions.register('staff-heading')}>
                        <SectionHeader title="Trainerteam"/>
                    </div>
                    {cards(staff)}
                </>
            )}
        </>
    );
};

export default Team;
